import Service from "@/service/api";
import menu from './menu'
import group from './group'
import permissionMatrix from './permission-matrix'
import audit from './audit'
import assignment from './assignment'
import { getPayload, mapMenu } from './shared'

function normalizePermissionPath(path) {
  if (!path) return ''
  let normalized = String(path).trim()
  const queryIndex = normalized.indexOf('?')
  if (queryIndex !== -1) normalized = normalized.slice(0, queryIndex)
  const hashIndex = normalized.indexOf('#')
  if (hashIndex !== -1) normalized = normalized.slice(0, hashIndex)
  normalized = normalized.replace(/\/{2,}/g, '/')
  if (!normalized.startsWith('/')) normalized = `/${normalized}`
  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1)
  }
  return normalized
}

function swapPermissionPlurality(path) {
  const normalized = normalizePermissionPath(path)
  if (!normalized) return ''
  if (normalized.includes('/permissions')) return normalized.replace('/permissions', '/permission')
  if (normalized.includes('/permission')) return normalized.replace('/permission', '/permissions')
  return ''
}

function normalizePermissionMatrix(matrix) {
  const source = matrix && typeof matrix === 'object' ? matrix : {}
  return Object.keys(source).reduce((acc, key) => {
    const normalizedKey = normalizePermissionPath(key)
    if (!normalizedKey) return acc
    acc[normalizedKey] = Object.assign(
      { all: false, view: false, edit: false, delete: false, action: false, logs: false },
      acc[normalizedKey] || {},
      source[key] || {}
    )
    return acc
  }, {})
}

const ServerModule = {
  namespaced: true,
  modules: {
    menu,
    group,
    permissionMatrix,
    audit,
    assignment
  },
  state: {
    matrix: {},
    assignments: [],
    permissions: [],
    catalogMenus: [],
    catalogLoadedAt: 0,
    catalogPendingRequest: null,
    loaded: false,
    loading: false,
    lastLoadedAt: 0,
    lastAccountId: null,
    pendingRequest: null
  },
  mutations: {
    setLoading(state, value) {
      state.loading = value;
    },
    setMyPermissions(state, payload) {
      state.matrix = normalizePermissionMatrix(payload && payload.matrix ? payload.matrix : {});
      state.assignments = payload && payload.assignments ? payload.assignments : [];
      state.permissions = payload && payload.permissions ? payload.permissions : [];
      state.loaded = true;
      state.lastLoadedAt = Date.now();
    },
    setCatalogMenus(state, menus) {
      state.catalogMenus = Array.isArray(menus) ? menus : [];
      state.catalogLoadedAt = Date.now();
    },
    setCatalogPendingRequest(state, request) {
      state.catalogPendingRequest = request;
    },
    setPendingRequest(state, request) {
      state.pendingRequest = request;
    },
    setLastAccountId(state, accountId) {
      state.lastAccountId = accountId || null;
    },
    reset(state) {
      state.matrix = {};
      state.assignments = [];
      state.permissions = [];
      state.catalogMenus = [];
      state.catalogLoadedAt = 0;
      state.catalogPendingRequest = null;
      state.loaded = false;
      state.loading = false;
      state.lastLoadedAt = 0;
      state.lastAccountId = null;
      state.pendingRequest = null;
    }
  },
  actions: {
    async fetchNavigationCatalog({ state, commit }, params = {}) {
      const force = !!params.force
      const cacheMaxAge = typeof params.cacheMaxAge === 'number' ? params.cacheMaxAge : 300000
      const isFresh = state.catalogMenus.length && (Date.now() - state.catalogLoadedAt) < cacheMaxAge

      if (!force && isFresh) {
        return state.catalogMenus
      }

      if (state.catalogPendingRequest && !force) {
        return state.catalogPendingRequest
      }

      const request = (async () => {
        try {
          const response = await Service.security('menus')
          const menus = (getPayload(response) || []).map(mapMenu)
          commit('setCatalogMenus', menus)
          return menus
        } finally {
          commit('setCatalogPendingRequest', null)
        }
      })()

      commit('setCatalogPendingRequest', request)
      return await request
    },
    async fetchMyPermissions({ state, commit, rootGetters }, params = {}) {
      const profile = rootGetters['auth/profile'] || null
      const accountId = params.accountId || params.id || (profile && (profile._id || profile.id)) || null
      const force = !!params.force
      const cacheMaxAge = typeof params.cacheMaxAge === 'number' ? params.cacheMaxAge : 60000
      const isSameAccount = state.lastAccountId && state.lastAccountId === accountId
      const isFresh = state.loaded && isSameAccount && (Date.now() - state.lastLoadedAt) < cacheMaxAge

      if (!force && isFresh) {
        return {
          data: {
            data: {
              matrix: state.matrix,
              assignments: state.assignments,
              permissions: state.permissions
            }
          }
        }
      }

      if (state.pendingRequest && !force) {
        return state.pendingRequest;
      }

      const request = (async () => {
        commit('setLoading', true);
        commit('setLastAccountId', accountId);
        try {
          const response = await Service.security('my-permissions', Object.assign({}, params, accountId ? { accountId } : {}));
          const data = response && response.data && response.data.data ? response.data.data : {};
          commit('setMyPermissions', data);
          return response;
        } finally {
          commit('setLoading', false);
          commit('setPendingRequest', null);
        }
      })();

      commit('setPendingRequest', request);
      return await request;
    }
  },
  getters: {
    matrix(state) {
      return state.matrix;
    },
    loaded(state) {
      return state.loaded;
    },
    catalogMenus(state) {
      return state.catalogMenus;
    },
    canAccess: (state) => (path, action = 'view', options = {}) => {
      if (!path) return true;
      const normalizedPath = normalizePermissionPath(path);
      const rule = state.matrix[normalizedPath] || state.matrix[swapPermissionPlurality(normalizedPath)];
      if (!rule) return !!(options && options.keepWhenMissing);
      return !!(rule.all || rule[action]);
    }
  }
};

export default ServerModule;
